'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import type { PageUrlMap } from '@/types/blog';
import { useCustomerPanelUrlBuilder, useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import { cardVariants, microHoverVariants, buttonVariants } from '@/lib/animations';

interface QuickActionsProps {
  language?: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  index?: number;
}

export default function QuickActions({
  language = 'en',
  translations = {},
  pageUrls,
  defaultLanguage = 'en',
  index = 0,
}: QuickActionsProps) {
  const t = useCustomerPanelTranslations(translations);
  const buildUrl = useCustomerPanelUrlBuilder(pageUrls, language, defaultLanguage);

  return (
    <motion.div 
      className="bg-white rounded-lg shadow-tour-card border border-gray-100 p-6 group group-hover:shadow-2xl transition-all duration-300 transition-shadow duration-500"
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      custom={index}
      whileHover="hover"
    >
      <h3 className="text-lg font-bold text-gray-900 mb-5">
        {t('customer_panel.dashboard.quick_actions', 'Quick Actions')}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <motion.div
          variants={microHoverVariants}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Link
            href={buildUrl('customer-reservations')}
            className="flex items-center gap-4 p-5 rounded-xl border-2 border-gray-200 hover:border-alsero-first hover:bg-gradient-to-br hover:from-alsero-first hover:to-red-600 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 group shadow-sm hover:shadow-md"
            aria-label={t('customer_panel.reservations.make_reservation', 'Make Reservation')}
          >
            <div className="flex-shrink-0 rounded-xl bg-gradient-to-br from-alsero-first to-red-600 p-3 shadow-lg group-hover:scale-110 transition-transform duration-300" aria-hidden="true">
              <PlusIcon className="h-6 w-6 text-white" />
            </div>
            <span className="text-sm font-semibold text-gray-700 group-hover:text-white transition-colors">
              {t('customer_panel.reservations.make_reservation', 'Make Reservation')}
            </span>
          </Link>
        </motion.div>
        
        <motion.div
          variants={microHoverVariants}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Link
            href={buildUrl('customer-profile')}
            className="flex items-center gap-4 p-5 rounded-xl border-2 border-gray-200 hover:border-alsero-first hover:bg-gradient-to-br hover:from-alsero-first hover:to-red-600 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 group shadow-sm hover:shadow-md"
            aria-label={t('customer_panel.sidebar.profile', 'My Profile')}
          >
            <div className="flex-shrink-0 rounded-xl bg-gradient-to-br from-alsero-first to-red-600 p-3 shadow-lg group-hover:scale-110 transition-transform duration-300" aria-hidden="true">
              <PencilIcon className="h-6 w-6 text-white" />
            </div>
            <span className="text-sm font-semibold text-gray-700 group-hover:text-white transition-colors">
              {t('customer_panel.sidebar.profile', 'My Profile')}
            </span>
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
}

